import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
} from "@mui/icons-material";
import translationService, {
  Translation,
} from "../../../api/services/translationService";
import TranslationForm from "./TranslationForm";
import { toast } from "react-toastify";

const TranslationsList: React.FC = () => {
  const [filteredTranslations, setFilteredTranslations] = useState<
    Translation[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [appTypeFilter, setAppTypeFilter] = useState(""); // New: App type filter
  const [categories, setCategories] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState<
    Translation | undefined
  >();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [translationToDelete, setTranslationToDelete] =
    useState<Translation | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState("");

  useEffect(() => {
    loadTranslations();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, categoryFilter, appTypeFilter]);

  const loadTranslations = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category = categoryFilter;
      if (appTypeFilter) params.app_type = appTypeFilter;

      const response = await translationService.getAll(params);
      setFilteredTranslations(response.data);
    } catch (error) {
      toast.error("Error loading translations");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await translationService.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const handleAdd = () => {
    setSelectedTranslation(undefined);
    setFormOpen(true);
  };

  const handleEdit = (translation: Translation) => {
    setSelectedTranslation(translation);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!translationToDelete) return;

    try {
      await translationService.delete(translationToDelete.id);
      toast.success("Translation deleted successfully");
      loadTranslations();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error("Error deleting translation");
    }
  };

  const handleExport = async (language?: string) => {
    try {
      const response = await translationService.exportTranslations(language);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `translations_${language || "all"}_${
        new Date().toISOString().split("T")[0]
      }.json`;
      a.click();
      toast.success("Translations exported successfully");
    } catch (error) {
      toast.error("Error exporting translations");
    }
  };

  const handleImport = async () => {
    try {
      const data = JSON.parse(importData);
      const translations = Array.isArray(data)
        ? data
        : Object.entries(data).map(([key, value]) => ({
            key,
            en: typeof value === "string" ? value : (value as any).en,
            ar: typeof value === "string" ? value : (value as any).ar,
            category: "general",
          }));

      const response = await translationService.bulkImport(translations, true);
      toast.success(
        `Import complete: ${response.data.created} created, ${response.data.updated} updated`
      );
      loadTranslations();
      setImportDialogOpen(false);
      setImportData("");
    } catch (error: any) {
      toast.error(error.message || "Error importing translations");
    }
  };

  const openDeleteDialog = (translation: Translation) => {
    setTranslationToDelete(translation);
    setDeleteDialogOpen(true);
  };

  return (
    <Box sx={{ p: 2, maxWidth: "100%", overflow: "hidden" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h5">Translation Management</Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport()}
          >
            Export All
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport("en")}
          >
            Export EN
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport("ar")}
          >
            Export AR
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Translation
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search translations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Filter by Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Filter by App Type"
                value={appTypeFilter}
                onChange={(e) => setAppTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Apps</MenuItem>
                <MenuItem value="user">User App</MenuItem>
                <MenuItem value="clinician">Clinician App</MenuItem>
                <MenuItem value="shared">Shared</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer
        component={Paper}
        sx={{ maxHeight: "calc(100vh - 300px)" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%", py: 1 }}>Key</TableCell>
              <TableCell sx={{ width: "25%", py: 1 }}>English</TableCell>
              <TableCell sx={{ width: "25%", py: 1 }}>Arabic</TableCell>
              <TableCell sx={{ width: "10%", py: 1 }}>App Type</TableCell>
              <TableCell sx={{ width: "10%", py: 1 }}>Category</TableCell>
              <TableCell align="right" sx={{ width: "10%", py: 1 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredTranslations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                  No translations found
                </TableCell>
              </TableRow>
            ) : (
              filteredTranslations.map((translation) => (
                <TableRow key={translation.id} hover>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontSize="0.75rem"
                      sx={{
                        wordBreak: "break-word",
                        lineHeight: 1.3,
                      }}
                    >
                      {translation.key}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontSize="0.75rem"
                      sx={{
                        wordBreak: "break-word",
                        lineHeight: 1.3,
                      }}
                    >
                      {translation.en}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontSize="0.75rem"
                      sx={{
                        wordBreak: "break-word",
                        direction: "rtl",
                        lineHeight: 1.3,
                      }}
                    >
                      {translation.ar}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      label={translation.appType || "user"}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                      color={
                        translation.appType === "clinician"
                          ? "secondary"
                          : translation.appType === "shared"
                          ? "info"
                          : "primary"
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      label={translation.category || "general"}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(translation)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(translation)}
                        sx={{ p: 0.5, ml: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TranslationForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        translation={selectedTranslation}
        onSuccess={loadTranslations}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the translation "
          {translationToDelete?.key}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Translations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste JSON data with translations. Format should be either:
            <br />
            1. Array: {`[{"key": "hello", "en": "Hello", "ar": "مرحبا"}]`}
            <br />
            2. Object: {`{"hello": "Hello", "welcome": "Welcome"}`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={importData}
            onChange={(e) => setImportData(e.target.value)}
            placeholder="Paste JSON here..."
            sx={{ fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleImport} variant="contained">
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranslationsList;
